import React from 'react';
import PropTypes from 'prop-types';
import Tooltip from '@material-ui/core/Tooltip';
import Button from '@material-ui/core/Button';
import { extendViewService } from 'tuc-webui-base/services/ViewService';
import SearchBar from 'tuc-webui-base/components/SearchBar';
import { withStoreData } from 'grb-webui/store';
import MessageDialog from 'grb-webui/components/MessageDialog';
import Form, { parseConfiguration, sameData } from 'grb-webui/components/Form';
import PreventTransitionDialog from 'grb-webui/components/PreventTransitionDialog';
import { withPageFilter } from 'grb-webui/components/FilterBar';
import AppMainPage, { FIT_TO_PAGE_VARIANT } from '../../components/AppMainPage';
import SettingsNavHOC from './SettingsNav';
import { SettingsForms } from './SettingsForm';
import { getClosestScrollItem, scrollTo } from './menu';
import './SettingsPage.scss';

export default (options) => {
  const {
    settingsEntity = 'settings',
    useCancel = true,
    iconsByName = {},
    componentsByName = {},
    pages: pageOptions = {},
    makeMenuId = id => `menu-${id}`,
    useSearch = false,
  } = options;

  const SettingsNav = SettingsNavHOC({ ...options, makeMenuId });

  const ViewServiceProperties = {
    iconsByName,
    componentsByName,
  };

  const computeConfiguration = (config, viewService, navBasePath) => {
    let pageIndex = 0;
    const makePageId = ({ id }) => {
      if (id) return id;
      pageIndex += 1;
      return `settings${pageIndex}`;
    };

    const computePage = (cfg, basePath) => {
      const { icon, label, pages, path, component, type, navig = true, ...formConfig } = cfg;
      const id = makePageId(formConfig);
      let Icon;
      if (icon) {
        Icon = viewService.getIcon(icon);
        if (!Icon) {
          console.error('Cannot find icon ', icon);
        }
      }

      let Component;
      if (component) {
        Component = viewService.getComponent(component);
        if (!Component) {
          console.error('Cannot find component ', component);
        }
      } else {
        Component = Form;
      }

      const pageOpts = formConfig.id && pageOptions[id] || undefined;

      const { validate, ...configuration } = parseConfiguration(formConfig, pageOpts, viewService);

      const pagePath = `${basePath}/${path || id}`;
      const cpPages = pages ? pages.map(childPage => computePage(childPage, pagePath)) : [];
      return {
        icon: Icon,
        label,
        id,
        path: pagePath,
        type,
        navig,
        accept: pageOpts && pageOptions.accept || undefined,
        configuration,
        Component,
        getPages: (pageOpts && pageOpts.getPages)
          ? (settings, props) => {
            const ids = pageOpts.getPages(settings, props, cpPages);
            return !ids ? [] : cpPages.filter(p => (ids.indexOf(p.id) >= 0));
          }
          : (() => cpPages),
        validate,
      };
    };

    const pages = config.pages
      ? config.pages.map(childPage => computePage(childPage, navBasePath))
      : [config];
    return { pages, hasNav: (pages.length > 1) || !!(pages[0].label || pages[0].icon) };
  };

  const prepareForEditing = options.prepareForEditing || ((settings, settingsService) => {
    return settingsService.prepareForEditing
      ? settingsService.prepareForEditing(settings)
      : JSON.parse(JSON.stringify(settings));
  });

  class Container extends React.PureComponent {
    static computePageStates(pages, settings, props, prevPageStates) {
      const findState = (states, id) => {
        return states && states.find((state) => {
          return state.id === id
            ? state
            : (state.children && state.children.length && findState(state.children, id) || false);
        });
      };
      const processPage = (page) => {
        if (!page.accept || page.accept(settings, props)) {
          // Look for prevState
          const prevState = findState(prevPageStates, page.id);

          // Validate page data
          const state = {
            ...page,
            // prevState.error: Optimization to keep references to old errors if unchanged
            errors: page.validate(settings, props, prevState && prevState.errors),
            children: page.getPages(settings, props).map(processPage).filter(x => x),
          };
          state.hasErrors = () => {
            return !!state.errors && Object.keys(state.errors).find(k => state.errors[k]);
          };
          return state;
        }
        return null;
      };
      return pages.map(processPage).filter(x => x);
    }

    static getDerivedStateFromProps(props, state) {
      let newState = null;
      let viewService;
      if (props.viewService !== state.viewServiceRef) {
        viewService = extendViewService(props.viewService, ViewServiceProperties);
        newState = { viewService, viewServiceRef: props.viewService };
      } else {
        viewService = state.viewService;
      }
      // If new configuration, update settings pages
      if ((props.configuration !== state.configuration) || (props.route !== state.route)) {
        newState = {
          ...(newState || {}),
          configuration: props.configuration,
          route: props.route,
          ...computeConfiguration(props.configuration, viewService, props.route.getFullPath({})),
        };
      }

      // If new settings
      const settings = props[settingsEntity];
      if (newState || (settings !== state.settings)) {
        if (!newState) {
          newState = {};
        }
        const { pages } = { ...state, ...newState };
        newState.editedSettings = prepareForEditing(settings, props.settingsService);
        newState.origSettings = prepareForEditing(settings, props.settingsService);
        newState.pageStates = Container.computePageStates(pages, newState.editedSettings, state.pageStates);
        newState.editSettingsMsg = props.settingsService.validateUpdateSettings(settings);
        if (newState.editSettingsMsg === true) {
          newState.editSettingsMsg = null;
        }
        newState.settings = settings;
      }

      return newState;
    }

    constructor(props) {
      super(props);

      this.state = {
        messageDialog: {},
      };

      this.navRef = React.createRef();
      this.scrollRef = React.createRef();

      this.handleReset = this.handleReset.bind(this);
      this.handleSubmit = this.handleSubmit.bind(this);
      this.handleCloseMessageDialog = this.handleCloseMessageDialog.bind(this);
      this.handleFormChange = this.handleFormChange.bind(this);
      this.updatePageURL = this.updatePageURL.bind(this);
    }

    componentDidMount() {
      this.scrollRef.current.addEventListener('scroll', this.updatePageURL);
      const pageId = this.getURLPageId();
      if (pageId) {
        this.scrollToPageId(pageId);
      }
    }

    getSnapshotBeforeUpdate(prevProps) {
      const pageId = this.getURLPageId();
      if (this.getURLPageId(prevProps) !== pageId) {
        return { pageId };
      }
      return null;
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
      if (snapshot) {
        this.scrollToPageId(snapshot.pageId);
      }
    }

    componentWillUnmount() {
      this.scrollRef.current.removeEventListener('scroll', this.updatePageURL);
    }

    getPage({ id, pathname }) {
      const findPage = (pages) => {
        let page;
        pages.find((p) => {
          if ((p.id === id) || (p.path === pathname)) {
            page = p;
            return p;
          }
          page = p.children && p.children.length && findPage(p.children) || null;
          return page;
        });
        return page;
      };
      return findPage(this.state.pageStates);
    }

    getURLPageId(props) {
      const { location } = props || this.props;
      const page = this.getPage({ pathname: location.pathname });
      return page && page.id;
    }

    scrollToPageId(pageId) {
      const section = pageId && document.getElementById(pageId);
      if (section && this.scrollRef.current) {
        scrollTo(section, this.scrollRef.current);
      }
    }

    updatePageURL() {
      const ids = [];
      const getIds = (pages) => {
        pages.filter(p => p.navig).forEach((p) => {
          ids.push(p.id);
          if (p.children) {
            getIds(p.children);
          }
        });
      };
      getIds(this.state.pageStates);
      const closestScollItem = getClosestScrollItem({
        mainElt: this.scrollRef.current,
        getScrollItems: () => ids.map(id => document.getElementById(id)).filter(x => x),
      });
      const page = closestScollItem && this.getPage({ id: closestScollItem.id });
      if (page) {
        const { search } = this.props;
        this.props.history.push(search ? `${page.path}?search=${search}` : page.path);
      }
    }

    setMessageDialog(params) {
      this.setState({ messageDialog: params });
    }

    handleReset() {
      this.props.onUpdateSettings();
    }

    handleSubmit() {
      const { editSettingsMsg, editedSettings, viewService } = this.state;
      if (editSettingsMsg) {
        this.setMessageDialog({
          title: 'Submit settings error',
          message: editSettingsMsg,
          open: true,
        });
      } else {
        this.props.settingsService.updateSettings(editedSettings)
        .then(() => {
          this.props.onSnackbarMessage({ message: 'Settings changed' });
        })
        .catch(e => viewService.onError(e));
      }
    }

    handleCloseMessageDialog() {
      this.setState({ messageDialog: { open: false } });
    }

    handleFormChange(editedSettings) {
      const { pages, pageStates } = this.state;
      this.setState({
        editedSettings,
        pageStates: Container.computePageStates(pages, editedSettings, this.props, pageStates),
      });
    }

    hasErrors(states) {
      return Boolean((states || this.state.pageStates || []).find((st) => {
        return st.hasErrors() || this.hasErrors(st.children);
      }));
    }

    render() {
      const {
        editedSettings,
        origSettings,
        pageStates,
        hasNav,
        editSettingsMsg,
        messageDialog,
        viewService,
      } = this.state;

      const hasErrors = this.hasErrors();
      const hasChanged = !sameData(origSettings, editedSettings, true);

      if (!pageStates || !pageStates.length) {
        return (null);
      }
      const selectedPageId = this.getURLPageId();
      //
      // Header
      //
      const HeaderBar = (
        <React.Fragment>
          {useSearch && (
            <SearchBar
              {...this.props}
              placeholder="Search settings..."
              value={this.props.search}
              onChange={this.props.onSearchChange}
            />
          )}
          <div className="grb-main-page-header-buttons">
            {useCancel && (
              <Tooltip title="Reset settings">
                <span>
                  <Button
                    color="primary"
                    variant="outlined"
                    aria-label="Cancel"
                    onClick={this.handleReset}
                    id="reset-settings-button"
                    disabled={!hasChanged}
                  >
                    Cancel
                  </Button>
                </span>
              </Tooltip>
            )}
            <Tooltip title="Update settings">
              <span>
                <Button
                  color="primary"
                  variant="contained"
                  aria-label="Submit"
                  onClick={this.handleSubmit}
                  id="submit-settings-button"
                  disabled={hasErrors || !hasChanged}
                >
                  Save
                </Button>
              </span>
            </Tooltip>
          </div>
        </React.Fragment>
      );

      return (
        <AppMainPage
          route={this.props.route}
          TrailingBar={HeaderBar}
          className={`grb-settings-page${useSearch ? ' grb-settings-page-search' : ''}`}
          variant={FIT_TO_PAGE_VARIANT}
        >
          <PreventTransitionDialog
            {...this.props}
            when={hasChanged}
            dialogKey="GrbSettingsPage"
          />
          <div className="grb-settings-ctnr">
            {hasNav && (
              <div className="grb-settings-nav-ctnr" ref={this.navRef}>
                <SettingsNav
                  pages={pageStates}
                  location={this.props.location}
                  selectedPage={!selectedPageId && pageStates[0] || undefined}
                />
              </div>
            )}
            <div className="grb-settings-content" ref={this.scrollRef}>
              <SettingsForms
                {...this.props}
                viewService={viewService}
                pages={pageStates}
                onFormChange={this.handleFormChange}
                data={editedSettings}
                readOnly={!!editSettingsMsg}
                search={this.props.search}
              />
            </div>
          </div>
          {messageDialog.open && (
            <MessageDialog
              title={messageDialog.title}
              message={messageDialog.message}
              onClose={this.handleCloseMessageDialog}
              open
            />
          )}
        </AppMainPage>
      );
    }
  }

  Container.propTypes = {
    [settingsEntity]: PropTypes.shape({}).isRequired,
    viewService: PropTypes.shape({
      onError: PropTypes.func,
      getComponent: PropTypes.func,
    }).isRequired,
    settingsService: PropTypes.shape({
      prepareForEditing: PropTypes.func,
      sameSettings: PropTypes.func,
      validateUpdateSettings: PropTypes.func,
      updateSettings: PropTypes.func,
    }).isRequired,
    settings: PropTypes.shape({}),
    configuration: PropTypes.shape({}).isRequired,
    onSnackbarMessage: PropTypes.func.isRequired,
    onUpdateSettings: PropTypes.func.isRequired,
    route: PropTypes.shape({
      matchPath: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.arrayOf(PropTypes.string),
      ]),
      getFullPath: PropTypes.func,
    }).isRequired,
    location: PropTypes.shape({
      pathname: PropTypes.string,
    }).isRequired,
    history: PropTypes.shape({
      push: PropTypes.func,
    }).isRequired,
    match: PropTypes.shape({
      params: PropTypes.shape({
        section: PropTypes.string,
      }),
    }).isRequired,
    search: PropTypes.string,
    onSearchChange: PropTypes.func,
  };

  Container.defaultProps = {
    settings: undefined,
    search: undefined,
    onSearchChange: undefined,
  };

  const StoredContainer = withStoreData(Container, {
    entities: [settingsEntity].concat(options.entities || []),
    onUpdateStore: 'onUpdateSettings',
  });
  if (!useSearch) {
    return StoredContainer;
  }
  return withPageFilter(StoredContainer, {
    searchKey: 'search',
    propertyEditors: [],
  });
};
